import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Play, Info, ArrowRight, TrendingUp } from "lucide-react";
import { api } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Row } from "../components/Row";
import { VideoCard } from "../components/VideoCard";
import { ShortThumb } from "../components/ShortThumb";
import { PhillySpotlight } from "../components/PhillySpotlight";
import { ContinueWatching } from "../components/ContinueWatching";

const HERO_ROTATE_MS = 6000;

export default function Landing() {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [trending, setTrending] = useState([]);
  const [heroIdx, setHeroIdx] = useState(0);
  const [rotateKey, setRotateKey] = useState(0);

  useEffect(() => {
    api.get("/home").then((r) => setFeed(r.data)).catch(() => {});
    api.get("/trending").then((r) => setTrending(r.data)).catch(() => {});
  }, []);

  // Auto-rotate the billboard through all channels every 6 seconds.
  // rotateKey is bumped on manual selection so the timer restarts (full dwell).
  useEffect(() => {
    if (feed.length <= 1) return;
    const timer = setInterval(() => {
      setHeroIdx((i) => (i + 1) % feed.length);
    }, HERO_ROTATE_MS);
    return () => clearInterval(timer);
  }, [feed.length, rotateKey]);

  const selectHero = (i) => {
    setHeroIdx(i);
    setRotateKey((k) => k + 1);
  };

  const hero = feed.length ? feed[heroIdx % feed.length] : null;
  const heroVideo = hero?.videos?.[0];
  const channelCount = feed.length;

  return (
    <div className="min-h-screen bg-[#030305]">
      <Navbar />

      {/* Billboard hero carousel */}
      <section className="relative h-[85vh] min-h-[600px] w-full overflow-hidden">
        {hero && (
          <motion.div
            key={`bg-${hero.channel.slug}`}
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: "easeInOut" }}
            className="absolute inset-0"
          >
            <img src={hero.channel.image} alt={hero.channel.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#030305] via-[#030305]/60 to-[#030305]/20" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#030305] via-[#030305]/50 to-transparent" />
          </motion.div>
        )}

        {/* Persistent network value line (stays while billboard rotates) */}
        <div className="absolute top-0 left-0 right-0 z-20 pt-[72px] md:pt-20" data-testid="network-tagline">
          <div className="max-w-[1600px] mx-auto px-6 md:px-10">
            <motion.div
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="max-w-xl"
            >
              <p className="text-[11px] md:text-xs font-bold uppercase tracking-[0.3em] text-white">
                <span className="text-[#FF2A2A]">QTV</span> Network
              </p>
              <p className="mt-1.5 text-sm md:text-base text-zinc-100 font-medium leading-snug">
                Free streaming TV. {channelCount} channels, zero subscriptions. Fresh episodes daily.
              </p>
              {!user && (
                <Link
                  to="/register"
                  data-testid="tagline-join-free"
                  className="group mt-2.5 inline-flex items-center gap-1.5 text-xs md:text-sm font-bold uppercase tracking-[0.15em] text-[#FF2A2A] hover:text-white transition-colors"
                >
                  Join Free
                  <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                </Link>
              )}
            </motion.div>
          </div>
        </div>

        <div className="relative z-10 h-full max-w-[1600px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-28 md:pb-32">
          {hero && (
            <motion.div
              key={`content-${hero.channel.slug}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-2xl"
            >
              <p className="text-xs tracking-[0.3em] uppercase text-[#FF2A2A] mb-4">{hero.channel.tagline}</p>
              <h1 className="font-display font-900 text-5xl sm:text-7xl leading-[0.9] uppercase tracking-tighter text-white">
                {hero.channel.name}
              </h1>
              <p className="mt-5 text-base md:text-lg text-zinc-300 leading-relaxed max-w-xl">
                {hero.channel.description}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                {heroVideo && (
                  <Link
                    to={hero.channel.format === "shorts" ? `/channel/${hero.channel.slug}` : `/watch/${heroVideo.id}`}
                    data-testid="hero-play-button"
                    className="flex items-center gap-2 bg-white hover:bg-white/90 text-black px-8 py-3.5 rounded-sm font-bold transition-colors"
                  >
                    <Play className="w-5 h-5 fill-black" /> {hero.channel.format === "shorts" ? "Watch Shorts" : "Play"}
                  </Link>
                )}
                <Link
                  to={`/channel/${hero.channel.slug}`}
                  data-testid="hero-info-button"
                  className="flex items-center gap-2 bg-white/15 hover:bg-white/25 backdrop-blur-md text-white px-8 py-3.5 rounded-sm font-semibold transition-colors"
                >
                  <Info className="w-5 h-5" /> More Info
                </Link>
              </div>
            </motion.div>
          )}
        </div>

        {/* Carousel indicator dots */}
        {feed.length > 1 && (
          <div className="absolute bottom-12 md:bottom-40 left-6 md:left-auto right-auto md:right-10 z-30 flex items-center gap-1.5 md:gap-2 bg-black/30 backdrop-blur-md rounded-full px-3 py-2" data-testid="hero-dots">
            {feed.map((item, i) => (
              <button
                key={item.channel.slug}
                data-testid={`hero-dot-${item.channel.slug}`}
                onClick={() => selectHero(i)}
                aria-label={`Show ${item.channel.name}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === heroIdx ? "w-6 md:w-8 bg-[#FF2A2A]" : "w-2.5 md:w-4 bg-white/50 hover:bg-white/80"
                }`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Channel rows */}
      <div className="relative z-10 space-y-10 md:space-y-12 pt-8 pb-20">
        <ContinueWatching />
        {trending.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Row
              testid="home-row-trending"
              title={
                <span className="flex items-center gap-2.5">
                  <TrendingUp className="w-6 h-6 text-[#FF2A2A]" strokeWidth={2.5} />
                  Trending on QTV
                </span>
              }
            >
              {trending.map((v, idx) => (
                <div key={v.id} className="relative w-[220px] md:w-[300px] shrink-0">
                  <span className="absolute -top-1 left-1 z-10 font-display font-900 text-4xl md:text-5xl leading-none text-white/15 select-none pointer-events-none">
                    {idx + 1}
                  </span>
                  <VideoCard video={v} />
                </div>
              ))}
            </Row>
          </motion.div>
        )}

        <PhillySpotlight />

        {feed.map((item, i) => (
          <motion.div
            key={item.channel.slug}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
          >
            <Row
              testid={`home-row-${item.channel.slug}`}
              title={item.channel.name}
              action={
                <Link
                  to={`/channel/${item.channel.slug}`}
                  data-testid={`home-explore-${item.channel.slug}`}
                  className="flex items-center gap-1 text-xs md:text-sm text-zinc-400 hover:text-white transition-colors"
                >
                  Explore <ArrowRight className="w-4 h-4" />
                </Link>
              }
            >
              {item.channel.format === "shorts"
                ? item.videos.map((v) => (
                    <ShortThumb key={v.id} video={v} to={`/channel/${item.channel.slug}`} />
                  ))
                : item.videos.map((v) => (
                    <div key={v.id} className="w-[220px] md:w-[300px] shrink-0">
                      <VideoCard video={v} />
                    </div>
                  ))}
            </Row>
          </motion.div>
        ))}
      </div>

      <footer className="border-t border-white/10 py-10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="font-display uppercase tracking-tighter">QTV<span className="text-[#FF2A2A]">Network</span></span>
          <p className="text-xs text-zinc-500">© 2026 QTV Network. Streaming free, everywhere.</p>
        </div>
      </footer>
    </div>
  );
}
